import { Link } from "react-router-dom";
import { ArrowRight, Check, TrendingUp, Eye, Zap } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

export default function VirtualStagingPage() {
  return (
    <main data-testid="virtual-staging-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-4 font-semibold">
                Our Most Popular Service
              </p>
              <h1
                data-testid="staging-headline"
                className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light text-[#0D2D54] leading-[1.1] tracking-tight mb-6"
              >
                Turn empty rooms into sale-ready spaces in 48 hours
              </h1>
              <p className="text-base text-[#6B7280] leading-relaxed mb-8 max-w-md">
                Professional virtual staging that helps estate agents, developers and landlords sell property faster and at a higher price.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/order"
                  data-testid="staging-order-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#0D2D54] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all"
                >
                  Order Now &mdash; From &pound;55/image
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div>
              <BeforeAfterSlider
                beforeImage="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/q4flx958_%C2%A9Km1423-015.jpg"
                afterImage="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/fcnds3p8_a7ce9d30c2bc32c8e70a95c518d24bd989bcff1f.jpg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Why Virtual Staging
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              The Smart Way to Sell Property
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                icon: TrendingUp,
                title: "Sell Faster",
                desc: "Staged homes sell 73% faster than empty ones. Help buyers visualise the potential of every room.",
              },
              {
                icon: Eye,
                title: "Increase Perceived Value",
                desc: "Virtual staging increases perceived property value by up to 20%, helping you achieve higher offers.",
              },
              {
                icon: Zap,
                title: "Stand Out Online",
                desc: "In a crowded market, staged photos get 40% more clicks on property portals like Rightmove and Zoopla.",
              },
            ].map((benefit, i) => (
              <div
                key={i}
                data-testid={`staging-benefit-${i}`}
                className="bg-[#FAFAF8] border border-[#E8E8E4] p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#758C7B]/15 border border-[#758C7B]/30 mb-5">
                  <benefit.icon size={20} className="text-[#0D2D54]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Our Work
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              Portfolio
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/e5e00aek_stocktonroadkitchenaltfinal.jpg", label: "Kitchen" },
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/v0qjqdwv_stocktonroadofficefinal.jpg", label: "Home Office" },
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/3s5sc4rd_stocktonroadbedroom2final.jpg", label: "Bedroom" },
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/nghc970d_KR%20bedroom%201.jpg", label: "Master Bedroom" },
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/fnl91iq3_7beechgrovebed2.jpg", label: "Guest Bedroom" },
              { src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mfir6td4_Manchester%20Road%20kitchen%201.jpg", label: "Open Plan Kitchen" },
            ].map((img, i) => (
              <div key={i} data-testid={`portfolio-${i}`} className="overflow-hidden group relative">
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-[#0D2D54]/0 group-hover:bg-[#0D2D54]/40 transition-all duration-300 flex items-end">
                  <span className="text-white text-sm font-medium px-4 pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {img.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Reminder */}
      <section className="py-20 lg:py-28 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-4">
            From just &pound;55 per image
          </h2>
          <p className="text-base text-white/50 mb-4">
            vs &pound;2,000+ for physical staging
          </p>
          <ul className="flex flex-wrap justify-center gap-6 mb-10 text-sm text-white/70">
            {["Delivered in 48 hours", "No subscription", "Only pay for what you need"].map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <Check size={14} className="text-[#758C7B]" />
                {item}
              </li>
            ))}
          </ul>
          <Link
            to="/order"
            data-testid="staging-bottom-cta"
            className="inline-flex items-center gap-2 bg-white text-[#0D2D54] px-10 py-4 text-sm font-semibold tracking-wide hover:bg-white/90 transition-all"
          >
            Upload your photos
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
